import { useState } from "react";
import { handleAuthExpired } from "../app/views/auth/auth.tsx";

const ENV_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim();
const IS_PRODUCTION = process.env.NODE_ENV === "production";

const API_BASE_CANDIDATES = (
  IS_PRODUCTION
    ? [ENV_API_BASE_URL]
    : [ENV_API_BASE_URL, "http://localhost:5001", "http://127.0.0.1:5001"]
).filter((url, index, arr) => arr.indexOf(url) === index);

let ACTIVE_API_BASE_URL = API_BASE_CANDIDATES[0] || "";

const buildNetworkError = (path, attempts) => {
  const attempted = attempts.join(", ");

  return new Error(
    `Unable to reach SimplAI backend for ${path}. Tried: ${attempted}. ` +
      `Start backend with 'npm run server' or set REACT_APP_API_BASE_URL correctly.`
  );
};

const requestWithBaseFallback = async (path, options = {}) => {
  const candidateList = [ACTIVE_API_BASE_URL, ...API_BASE_CANDIDATES].filter(
    (url, index, arr) => arr.indexOf(url) === index
  );

  const requestOptions = {
    credentials: "include",
    ...options,
  };

  const attempted = [];
  let lastNetworkError = null;

  for (const baseUrl of candidateList) {
    const finalUrl = `${baseUrl}${path}`;
    attempted.push(baseUrl);

    try {
      const res = await fetch(finalUrl, requestOptions);

      if (!res.ok) {
        const details = await res.text().catch(() => "");

        if (res.status === 401) {
          handleAuthExpired();
          throw new Error("Unauthorized: please log in again.");
        }

        throw new Error(
          `${path} failed with ${res.status} ${res.statusText}${
            details ? `: ${details}` : ""
          }`
        );
      }

      ACTIVE_API_BASE_URL = baseUrl;
      return res;
    } catch (err) {
      if (err instanceof TypeError) {
        lastNetworkError = err;
        continue;
      }

      throw err;
    }
  }

  const fallbackError = buildNetworkError(path, attempted);

  if (lastNetworkError) {
    fallbackError.cause = lastNetworkError;
  }

  throw fallbackError;
};

const extractConversationIds = (postRes) => {
  const cId =
    postRes?.result?.conversation_id ||
    postRes?.result?.conversationId ||
    postRes?.result?.cId ||
    postRes?.result?.cid ||
    postRes?.result?.c_id ||
    postRes?.data?.conversation_id ||
    postRes?.data?.conversationId ||
    postRes?.data?.cId ||
    postRes?.data?.cid ||
    postRes?.data?.c_id ||
    postRes?.conversation_id ||
    postRes?.conversationId ||
    postRes?.cId ||
    postRes?.cid ||
    postRes?.c_id;

  const mId =
    postRes?.result?.message_id ||
    postRes?.result?.messageId ||
    postRes?.result?.mId ||
    postRes?.result?.mid ||
    postRes?.result?.m_id ||
    postRes?.data?.message_id ||
    postRes?.data?.messageId ||
    postRes?.data?.mId ||
    postRes?.data?.mid ||
    postRes?.data?.m_id ||
    postRes?.message_id ||
    postRes?.messageId ||
    postRes?.mId ||
    postRes?.mid ||
    postRes?.m_id;

  return {
    cId,
    mId,
  };
};

const stringifySafe = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const readStorageArray = (key) => {
  try {
    const value = localStorage.getItem(key);

    if (!value) {
      return [];
    }

    const parsed = JSON.parse(value);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getValueFromRow = (row, possibleKeys) => {
  for (const key of possibleKeys) {
    const value = row?.[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};

const toCleanString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const buildCostModelRowsFromSource = (sourceRows) => {
  return sourceRows
    .map((row) => {
      const lineItemDescription = getValueFromRow(row, [
        "Line_Item_Description",
        "Line Item Description",
        "line_item_description",
        "LineItemDescription",
        "Item_Description",
        "Item Description",
        "Product",
        "Product_Name",
        "Product Name",
      ]);

      const supplierRegion = getValueFromRow(row, [
        "Supplier_Region",
        "Supplier Region",
        "supplier_region",
        "Region",
        "Country",
        "Supplier_Country",
        "Supplier Country",
      ]);

      const qty = getValueFromRow(row, [
        "Qty",
        "Quantity",
        "quantity",
        "Order_Qty",
        "Order Qty",
        "Volume",
      ]);

      const currency = getValueFromRow(row, [
        "Currency",
        "currency",
        "Currency_Code",
        "Currency Code",
      ]);

      return {
        Line_Item_Description: toCleanString(lineItemDescription),
        Supplier_Region: toCleanString(supplierRegion),
        Qty: toCleanString(qty),
        Currency: toCleanString(currency),
      };
    })
    .filter((row) => {
      return (
        row.Line_Item_Description ||
        row.Supplier_Region ||
        row.Qty ||
        row.Currency
      );
    });
};

const isCostModelPayloadBlankOrInvalid = (jsonData) => {
  const rows = jsonData?.data?.input_data;

  if (!Array.isArray(rows) || rows.length === 0) {
    return true;
  }

  return rows.every((row) => {
    const lineItem = toCleanString(row?.Line_Item_Description);
    const region = toCleanString(row?.Supplier_Region);
    const qty = toCleanString(row?.Qty);
    const currency = toCleanString(row?.Currency);

    return !lineItem && !region && !qty && !currency;
  });
};

const normalizeCostModelPayload = (jsonData) => {
  const incomingRows = Array.isArray(jsonData?.data?.input_data)
    ? jsonData.data.input_data
    : [];

  const incomingMappedRows = buildCostModelRowsFromSource(incomingRows);

  if (
    incomingMappedRows.length > 0 &&
    !isCostModelPayloadBlankOrInvalid(jsonData)
  ) {
    return {
      data: {
        input_data: incomingMappedRows,
      },
    };
  }

  const simplAIReadyRows = readStorageArray("spendiq_simplai_ready_data");
  const cleanedRows = readStorageArray("spendiq_cleaned_excel_data");
  const legacyRows = readStorageArray("spendiq_uploaded_excel_data");

  const sourceRows =
    simplAIReadyRows.length > 0
      ? simplAIReadyRows
      : cleanedRows.length > 0
      ? cleanedRows
      : legacyRows;

  const rebuiltRows = buildCostModelRowsFromSource(sourceRows);

  console.log("🔧 COST MODEL PAYLOAD WAS BLANK. REBUILT FROM STORAGE:", {
    sourceRowsCount: sourceRows.length,
    rebuiltRowsCount: rebuiltRows.length,
    firstSourceRow: sourceRows[0] || null,
    firstRebuiltRow: rebuiltRows[0] || null,
  });

  return {
    data: {
      input_data: rebuiltRows,
    },
  };
};

export const useSimplAI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildPayload = (jsonData, toolId = 17) => ({
    tool_id: "68bfcbe9c0f7b4af3a9cc547",
    cust_attr: {},
    inputs: {
      data: jsonData?.data || {},
      tool_id: toolId,
    },
  });

  const postToSimplAI = async (payload) => {
    const res = await requestWithBaseFallback("/simplai/post", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    return res.json();
  };

  const getFromSimplAIRaw = async (cId, mId, isStandalone = false) => {
    const res = await requestWithBaseFallback(
      `/simplai/get?cId=${encodeURIComponent(cId)}&mId=${encodeURIComponent(
        mId
      )}&isStandalone=${encodeURIComponent(String(isStandalone))}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, max-age=0",
          Pragma: "no-cache",
        },
      }
    );

    const responseData = await res.json();

    console.log("✅ RAW SIMPLAI GET RESPONSE:", responseData);

    return {
      data: responseData?.data || responseData,
      stream_status: String(responseData?.stream_status || "0")
        .trim()
        .toLowerCase(),
      trace_id:
        responseData?.trace_id ||
        responseData?.headers?.trace_id ||
        responseData?.traceId ||
        null,
      isStandalone: Boolean(responseData?.isStandalone),
    };
  };

  const pollSimplAI = async (cId, mId, isStandalone = false) => {
    let retries = 200;
    let attemptCount = 0;

    while (retries >= 0) {
      attemptCount++;

      const elapsedMinutes = ((attemptCount * 5) / 60).toFixed(1);

      console.log(
        `\n⏳ Polling attempt ${attemptCount} (${elapsedMinutes}min elapsed)`
      );

      try {
        const {
          data,
          stream_status,
          trace_id,
          isStandalone: returnedIsStandalone,
        } = await getFromSimplAIRaw(cId, mId, isStandalone);

        console.log("✅ GET DATA:", data);
        console.log("📡 Stream Status:", stream_status);
        console.log("📌 Trace ID:", trace_id);
        console.log("📌 isStandalone:", returnedIsStandalone);

        if (stream_status === "1") {
          console.log("⏳ Status 1: Agent still processing...");
          await new Promise((res) => setTimeout(res, 5000));
          retries--;
          continue;
        }

        if (stream_status === "2") {
          console.log("✅✅✅ Status 2: STREAM COMPLETE!");

          return {
            data,
            trace_id,
            isStandalone: returnedIsStandalone,
          };
        }

        if (stream_status === "0") {
          console.log("⏳ Status 0: Processing in progress...");
          await new Promise((res) => setTimeout(res, 5000));
          retries--;
          continue;
        }

        console.log(`⚠️ UNKNOWN STREAM STATUS: "${stream_status}"`);

        await new Promise((res) => setTimeout(res, 5000));
        retries--;
      } catch (pollErr) {
        console.error(
          `❌ Poll attempt ${attemptCount} error:`,
          pollErr?.message || pollErr
        );

        if (retries === 0) {
          throw pollErr;
        }

        retries--;
        await new Promise((res) => setTimeout(res, 5000));
      }
    }

    throw new Error("Max retries exceeded - Agent processing timeout");
  };

  const runSimplAI = async (jsonData, toolId = 17, options = {}) => {
    setLoading(true);
    setError(null);

    const isStandalone = Boolean(options?.isStandalone);
    const onPostResponse =
      typeof options?.onPostResponse === "function"
        ? options.onPostResponse
        : null;
    const onPollComplete =
      typeof options?.onPollComplete === "function"
        ? options.onPollComplete
        : null;

    try {
      console.log("\n🚀 STARTING SIMPLAI FLOW...");
      console.log("📌 Tool ID:", toolId);
      console.log("📌 isStandalone:", isStandalone);
      console.log("🌐 SimplAI backend URL:", ACTIVE_API_BASE_URL);

      const finalJsonData =
        Number(toolId) === 52 ? normalizeCostModelPayload(jsonData) : jsonData;

      if (Number(toolId) === 52) {
        console.log(
          "✅ FINAL COST MODEL JSON AFTER NORMALIZATION:",
          finalJsonData
        );
      }

      console.log("📦 SIMPLAI INPUT DATA SAMPLE:", {
        rowCount: finalJsonData?.data?.input_data?.length || 0,
        columns: finalJsonData?.data?.input_data?.[0]
          ? Object.keys(finalJsonData.data.input_data[0])
          : [],
        firstRow: finalJsonData?.data?.input_data?.[0] || null,
      });

      const payload = buildPayload(finalJsonData, toolId);

      console.log("✅ SIMPLAI POST PAYLOAD:", payload);
      
      const postRes = await postToSimplAI(payload);

      console.log("✅ POST RESPONSE:", postRes);
      console.log("✅ POST RESPONSE FULL:", stringifySafe(postRes));

      if (postRes?.error) {
        throw new Error(
          typeof postRes.error === "string"
            ? postRes.error
            : stringifySafe(postRes.error)
        );
      }

      if (
        postRes?.message &&
        String(postRes.message).toLowerCase().includes("unauthorized")
      ) {
        throw new Error(postRes.message);
      }

      const { cId, mId } = extractConversationIds(postRes);

      console.log("✅ extracted cId:", cId);
      console.log("✅ extracted mId:", mId);

      if (!cId || !mId) {
        console.error("❌ Missing cId/mId. Full SimplAI post response:", postRes);

        throw new Error(
          `Missing cId or mId from SimplAI response. Response keys: ${Object.keys(
            postRes || {}
          ).join(", ")}`
        );
      }

      if (onPostResponse) {
        await onPostResponse({
          cId,
          mId,
          postResponse: postRes,
          simplaiPayload: payload,
          inputData: finalJsonData?.data?.input_data || [],
          isStandalone,
        });
      }

      const {
        data: finalData,
        trace_id,
        isStandalone: returnedIsStandalone,
      } = await pollSimplAI(cId, mId, isStandalone);

      console.log("\n✅✅✅ SIMPLAI PROCESSING COMPLETE!");
      console.log("✅ FINAL RESULT:", finalData);
      console.log("📌 isStandalone returned:", returnedIsStandalone);

      if (trace_id) {
        console.log("📌 TRACE ID:", trace_id);
      }

      if (onPollComplete) {
        await onPollComplete({
          cId,
          mId,
          data: finalData,
          trace_id,
          isStandalone: returnedIsStandalone,
        });
      }

      setLoading(false);

      return {
        data: finalData,
        trace_id,
        isStandalone: returnedIsStandalone,
      };
    } catch (err) {
      console.error("\n❌ SimplAI Error:", err);

      setError(err?.message || "SimplAI failed");
      setLoading(false);

      throw err;
    }
  };

  const runDataCleaning = async (jsonData, options = {}) =>
    runSimplAI(jsonData, 17, options);

  const runSupplierConsolidation = async (jsonData, options = {}) =>
    runSimplAI(jsonData, 11, options);

  const runCostModelAnalysis = async (jsonData, options = {}) =>
    runSimplAI(jsonData, 52, options);

  const runSpendAIRisk = async (jsonData, toolId, options = {}) =>
    runSimplAI(jsonData, toolId, options);

  return {
    runSimplAI,
    runDataCleaning,
    runSupplierConsolidation,
    runCostModelAnalysis,
    runSpendAIRisk,
    loading,
    error,
  };
};